// Static blog content for Phase 1 (no CMS yet)
// Add a new article by appending to POSTS. Each post needs: slug, title, excerpt, category, readTime, date, cover, body (string of paragraphs separated by \n\n; ## for headings; - for bullets).

export const BLOG_CATEGORIES = [
  "Health Claims",
  "Motor Claims",
  "Life Claims",
  "Property & Fire",
  "IRDAI Updates",
  "Success Stories",
];

export const POSTS = [
  {
    slug: "health-claim-rejected-pre-existing",
    title: "Health Claim Rejected as Pre-existing? Here's What You Can Do",
    excerpt: "One of the most common reasons insurers reject health claims is the 'pre-existing disease' clause. Here is how to challenge it correctly.",
    category: "Health Claims",
    readTime: "6 min",
    date: "2026-05-18",
    cover: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1200&q=70",
    author: "Accurate Claims Team",
    body: `Health insurance rejections citing "pre-existing disease" (PED) are one of the most common — and one of the most frequently *wrongful* — claim denials in India.

Insurers often invoke the PED clause whenever the policyholder is diagnosed within the first two to four years of the policy. But the law and IRDAI guidelines are very clear: a condition is only a PED if it was *diagnosed* or *treated* before the policy was bought.

## When a PED rejection is wrongful

- The illness was first detected only after the policy started.
- There is no medical record showing diagnosis or treatment before the policy.
- The insurer relies on a single suggestive line in a discharge summary without supporting evidence.
- The waiting period for the specific condition has expired.

## How we build the appeal

1. **Re-read the policy wording.** PED definitions vary widely across insurers. Half the rejections fall apart at this step alone.
2. **Reconstruct the medical chronology.** We work with the treating doctors to issue clarifying letters — when did the symptoms begin, when was the diagnosis made.
3. **Pull the cumulative bonus & continuity history.** If the policy was ported, the waiting period rules change.
4. **Draft a structured representation** quoting the exact clauses, IRDAI master circulars and previous Ombudsman orders.

## What is the likely outcome?

In our experience, **roughly 6 in 10 wrongful PED rejections** are reversed at the insurer level once a proper representation is made. The remaining are usually settled either at the Internal Ombudsman or the Insurance Ombudsman — both free routes for the policyholder.

A rejection letter is not the end of the road. Read the policy wording carefully and act within the time limits.`,
  },
  {
    slug: "motor-claim-short-settlement",
    title: "Motor Claim Short-settled? Three Steps to Push Back",
    excerpt: "Insurer offering less than the actual loss? Don't accept the cheque yet. Three concrete steps to challenge a short settlement.",
    category: "Motor Claims",
    readTime: "5 min",
    date: "2026-04-22",
    cover: "https://images.unsplash.com/photo-1605152276897-4f618f831968?auto=format&fit=crop&w=1200&q=70",
    author: "Accurate Claims Team",
    body: `Short settlement is when an insurer offers significantly less than the actual loss assessed in your claim — often without a clear written justification.

## Step 1: Get the surveyor's report

Under IRDAI rules, the surveyor's report is a discoverable document. You are entitled to a copy. Request it formally in writing.

## Step 2: Reconcile the deductions

Most short settlements come from three deduction categories:
- **Depreciation** — often applied to parts that should not depreciate (plastic, glass, paintwork).
- **Salvage** — assumed value that may be inflated.
- **Betterment** — clauses applied without basis.

## Step 3: File a structured representation

A simple email saying "I disagree" is not enough. The representation must cite the surveyor's specific line items, the policy wording on each, and request a re-calculation.

If the insurer does not respond satisfactorily within 30 days, the matter can be escalated to the Insurance Ombudsman free of cost.`,
  },
  {
    slug: "life-claim-death-claim-process",
    title: "Life Insurance Death Claim — A Step-by-Step Guide",
    excerpt: "Filing a life insurance death claim is bureaucratic and emotional. Here is the document checklist and process timeline.",
    category: "Life Claims",
    readTime: "7 min",
    date: "2026-03-30",
    cover: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=70",
    author: "Accurate Claims Team",
    body: `Filing a life insurance death claim involves emotional strain, multiple documents, and strict timelines. Here is a calm checklist of what to do.

## Documents to keep ready

- Original policy document(s).
- Death certificate issued by the municipal authority.
- Cause-of-death certificate (Form A from hospital or doctor).
- Nominee KYC: PAN, Aadhaar, bank account proof, photograph.
- FIR + post-mortem report if death was accidental or unnatural.
- Latest premium receipt.
- Discharge summary if the deceased was hospitalised.

## Process timeline

1. Intimation — within 30 days of the event.
2. Document submission — usually 60–90 days, depending on the insurer.
3. Insurer investigation — for non-natural deaths or large sums assured, expect 30–90 days of underwriting review.
4. Decision — IRDAI mandates a 30-day decision window after all documents are received.

## Common rejection reasons

- Misrepresentation at the time of buying the policy.
- Lapsed policy (premium not paid).
- Death within the first two years (subject to investigation).

Each of these can be challenged with proper evidence. Don't accept a rejection without a written reason.`,
  },
  {
    slug: "fire-claim-property-loss",
    title: "Fire & Property Claims: Documenting the Loss Correctly",
    excerpt: "A documentation-heavy guide on how to build a strong fire or property damage claim file.",
    category: "Property & Fire",
    readTime: "5 min",
    date: "2026-02-12",
    cover: "https://images.unsplash.com/photo-1568667256549-094345857637?auto=format&fit=crop&w=1200&q=70",
    author: "Accurate Claims Team",
    body: `Fire and property damage claims are won or lost on documentation. The first 72 hours after the loss are the most important.

## Within the first 24 hours

- Inform the insurer in writing (email + courier).
- File an FIR if there is suspicion of arson, theft or third-party negligence.
- Take photographs and video — wide shots, room-by-room, close-ups of damaged items.
- Get the fire department report.

## Within 7 days

- Prepare an inventory of every damaged item with quantity, age, original purchase value and current replacement cost.
- Pull all purchase invoices, GST bills and bank statements.
- Engage a chartered accountant for loss-of-profit cover.

## During the survey

The insurer will appoint a surveyor. You can — and should — engage your own loss assessor in parallel. They can build a structured "claim book" that the surveyor uses as the starting point.

## What we have learned

Across hundreds of property claims, the difference between a poor settlement and a fair one is almost always paperwork — not the policy.`,
  },
  {
    slug: "irdai-2025-master-circular-summary",
    title: "What IRDAI's 2025 Master Circular Means for Policyholders",
    excerpt: "A plain-English summary of the IRDAI 2025 master circular on health insurance — and how it changes claim rights.",
    category: "IRDAI Updates",
    readTime: "8 min",
    date: "2026-01-08",
    cover: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=70",
    author: "Accurate Claims Team",
    body: `The IRDAI's master circular on health insurance — issued in 2024 and tightened in 2025 — has materially expanded policyholder rights. A quick summary.

## Cashless approval timelines

Insurers now have to approve initial cashless authorisation within **one hour** of receiving the request and final discharge within **three hours**. Hospitals must hand over the discharge summary along with the claim form.

## Standardised exclusions

Insurers can no longer add exclusions outside the IRDAI's master list. If your policy has a non-standard exclusion buried in the fine print, it is challengeable.

## Moratorium period

After 60 months of continuous coverage, claims **cannot** be repudiated on grounds of non-disclosure or misrepresentation — except for proven fraud.

## What it means for rejections

If your claim was rejected after the moratorium period for reasons other than fraud, you have a strong case for appeal. We've seen multiple such rejections reversed in 2025.

The full circular is on the IRDAI website. We recommend every senior policyholder skim through it once.`,
  },
  {
    slug: "case-study-mediclaim-rebuild",
    title: "Case Study: Rebuilding a ₹4.2L Mediclaim From a 'Final Rejection'",
    excerpt: "A real-life walkthrough — from final-rejection letter to full settlement in six weeks.",
    category: "Success Stories",
    readTime: "4 min",
    date: "2025-12-04",
    cover: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=70",
    author: "Accurate Claims Team",
    body: `Names and identifiers are changed for privacy.

## The case

A 52-year-old policyholder from Ahmedabad was admitted for a cardiac procedure. The insurer rejected the ₹4,17,500 claim citing pre-existing disease and "material non-disclosure".

## What we found

- The condition (hypertension) was first diagnosed *after* the policy was issued. The treating doctor's letter confirmed this.
- The insurer's rejection letter quoted clauses that did not match the actual policy wording.
- The "non-disclosure" was a routine fitness certificate from an annual employer check-up — not a diagnostic record.

## What we did

1. Pulled three years of medical records and the doctor's clarification letter.
2. Compared the rejection letter line-by-line with the policy wording — found two material misquotes.
3. Drafted a structured representation citing the IRDAI Master Circular 2024 and two Ombudsman orders.
4. Filed with the insurer's Internal Ombudsman.

## Outcome

Full settlement of ₹4,17,500 within 6 weeks of representation. No legal proceedings required.

This is the most common kind of case we see — the policyholder had a fair claim and a fair policy. Only the paperwork was missing.`,
  },
];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
